'use client';

import { createRevealObserver } from '@/lib/reveal-observer';
import React, { useEffect, useRef } from 'react';
import Link from 'next/link';

const services = [
  {
    number: '01',
    title: 'Fundraising Strategy',
    description:
      'Determine what capital is required, how much to raise, the appropriate financing stage, target investor profile, fundraising timeline and capital deployment strategy.',
    bullets: [
      'Capital requirement analysis',
      'Financing stage determination',
      'Investor profile targeting',
      'Timeline planning',
    ],
    wide: true,
  },
  {
    number: '02',
    title: 'Investor Positioning',
    description:
      'We help founders develop an investment narrative that makes the answers to critical investor questions clear and compelling.',
    bullets: ['Why this problem?', 'Why now?', 'Why this team?', 'Why invest now?'],
    wide: false,
  },
  {
    number: '03',
    title: 'Investor Targeting',
    description:
      'Not every investor is the right investor. We identify investors based on stage, sector, geography, ticket size, investment thesis and portfolio fit.',
    bullets: ['Stage & sector alignment', 'Ticket size matching', 'Portfolio fit analysis'],
    wide: false,
  },
  {
    number: '04',
    title: 'Investor Access',
    description:
      'Through our network, we facilitate connections with relevant capital providers across angels, family offices, VCs, strategic investors and syndicates.',
    bullets: ['Angel investors', 'Family offices', 'Venture capital funds', 'Strategic investors'],
    wide: false,
  },
  {
    number: '05',
    title: 'Fundraising Support',
    description:
      'We help founders navigate the process from the first investor conversation through the fundraising cycle — from outreach to closing.',
    bullets: ['Pipeline management', 'Meeting preparation', 'Round momentum', 'Closing strategy'],
    wide: false,
  },
];

export default function WhatWeDoSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = createRevealObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.scroll-fade').forEach((el) => {
              el.classList.add('visible');
            });
          }
        });
      },
      { rootMargin: '0px 0px -60px 0px', threshold: 0.05 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="what-we-do"
      className="py-24 px-6 border-t border-primary/10 bg-secondary/20"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
          <div className="max-w-2xl">
            <p className="section-label scroll-fade mb-5">What We Do</p>
            <h2 className="text-section-title font-sans font-semibold text-foreground scroll-fade scroll-fade-delay-1">
              We help companies turn fundraising{' '}
              <span className="font-serif italic text-primary">into a strategy.</span>
            </h2>
          </div>
          <p className="text-sm text-muted-foreground font-light leading-relaxed max-w-sm border-l border-primary/20 pl-6 scroll-fade scroll-fade-delay-2">
            Our role extends beyond making introductions. We work across the fundraising journey to
            help companies become better prepared for the market they are entering.
          </p>
        </div>

        {/* Bento grid */}
        {/* BENTO AUDIT: 5 services */}
        {/* Row 1: [col-1+col-2: Strategy cs-2] [col-3: Positioning cs-1] */}
        {/* Row 2: [col-1: Targeting cs-1] [col-2: Access cs-1] [col-3: Support cs-1] */}
        {/* Placed 5/5 ✓ */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Card 01 — wide (col-span-2) */}
          <div className="md:col-span-2 bento-card rounded-3xl p-8 scroll-fade flex flex-col justify-between min-h-64">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <span className="font-serif italic text-primary text-sm font-medium">
                  {services?.[0]?.number}
                </span>
                <div className="gold-rule" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-3">{services?.[0]?.title}</h3>
              <p className="text-sm text-muted-foreground font-light leading-relaxed mb-6 max-w-lg">
                {services?.[0]?.description}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {services?.[0]?.bullets?.map((b) => (
                <span
                  key={b}
                  className="text-xs px-3 py-1.5 rounded-full border border-primary/20 text-primary/80 bg-primary/5"
                >
                  {b}
                </span>
              ))}
            </div>
          </div>

          {/* Card 02 */}
          <div className="bento-card rounded-3xl p-8 scroll-fade scroll-fade-delay-1 flex flex-col justify-between min-h-64">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <span className="font-serif italic text-primary text-sm font-medium">
                  {services?.[1]?.number}
                </span>
                <div className="gold-rule" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-3">{services?.[1]?.title}</h3>
              <p className="text-sm text-muted-foreground font-light leading-relaxed mb-6">
                {services?.[1]?.description}
              </p>
            </div>
            <div className="space-y-1.5">
              {services?.[1]?.bullets?.map((b) => (
                <p key={b} className="text-xs text-muted-foreground flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-primary shrink-0" />
                  {b}
                </p>
              ))}
            </div>
          </div>

          {/* Card 03 */}
          <div className="bento-card rounded-3xl p-8 scroll-fade scroll-fade-delay-1 flex flex-col justify-between min-h-56">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <span className="font-serif italic text-primary text-sm font-medium">
                  {services?.[2]?.number}
                </span>
                <div className="gold-rule" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-3">{services?.[2]?.title}</h3>
              <p className="text-sm text-muted-foreground font-light leading-relaxed mb-6">
                {services?.[2]?.description}
              </p>
            </div>
            <div className="space-y-1.5">
              {services?.[2]?.bullets?.map((b) => (
                <p key={b} className="text-xs text-muted-foreground flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-primary shrink-0" />
                  {b}
                </p>
              ))}
            </div>
          </div>

          {/* Card 04 */}
          <div className="bento-card rounded-3xl p-8 scroll-fade scroll-fade-delay-2 flex flex-col justify-between min-h-56">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <span className="font-serif italic text-primary text-sm font-medium">
                  {services?.[3]?.number}
                </span>
                <div className="gold-rule" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-3">{services?.[3]?.title}</h3>
              <p className="text-sm text-muted-foreground font-light leading-relaxed mb-6">
                {services?.[3]?.description}
              </p>
            </div>
            <div className="space-y-1.5">
              {services?.[3]?.bullets?.map((b) => (
                <p key={b} className="text-xs text-muted-foreground flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-primary shrink-0" />
                  {b}
                </p>
              ))}
            </div>
          </div>

          {/* Card 05 */}
          <div className="bento-card rounded-3xl p-8 scroll-fade scroll-fade-delay-3 flex flex-col justify-between min-h-56">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <span className="font-serif italic text-primary text-sm font-medium">
                  {services?.[4]?.number}
                </span>
                <div className="gold-rule" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-3">{services?.[4]?.title}</h3>
              <p className="text-sm text-muted-foreground font-light leading-relaxed mb-6">
                {services?.[4]?.description}
              </p>
            </div>
            <div className="space-y-1.5">
              {services?.[4]?.bullets?.map((b) => (
                <p key={b} className="text-xs text-muted-foreground flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-primary shrink-0" />
                  {b}
                </p>
              ))}
            </div>
          </div>
        </div>

        {/* CTA row */}
        <div className="mt-12 flex justify-center scroll-fade">
          <Link
            href="/contact"
            className="px-8 py-4 rounded-full bg-primary text-primary-foreground text-sm font-semibold tracking-widest uppercase hover:bg-accent transition-all duration-300"
          >
            Start a Conversation
          </Link>
        </div>
      </div>
    </section>
  );
}
